import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { computed, onMounted, ref } from 'vue';

/**
 * Ruut Billing Composable - Adapted for new Chatwoot architecture
 * Uses account custom_attributes for subscription state instead of separate API
 * Maintains backward compatibility with legacy interface
 * Implements caching to prevent paywall flickering
 */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

export function useRuutBilling() {
  const { accountId, getAccount, currentAccount, isOnChatwootCloud } =
    useAccount();
  const { isEnterprise } = useConfig();
  const store = useStore();

  // Get limits from account (like useCaptain does for captain limits)
  const billingLimits = computed(() => {
    if (currentAccount.value?.limits) {
      return currentAccount.value.limits;
    }
    return null;
  });

  const billingUsage = computed(() => {
    if (currentAccount.value?.usage) {
      return currentAccount.value.usage;
    }
    return null;
  });

  const subscriptionInfo = computed(() => {
    if (currentAccount.value) {
      return {
        planName: currentAccount.value.plan_name,
        subscriptionStatus: currentAccount.value.subscription_status,
        subscriptionExpiresAt: currentAccount.value.subscription_expires_at,
        features: currentAccount.value.features || [],
      };
    }
    return null;
  });

  // Reactive state - initialize from cache to prevent flickering
  const cachedData = LocalStorage.getFromJsonStore(
    LOCAL_STORAGE_KEYS.RUUT_SUBSCRIPTION,
    accountId.value
  );

  const subscription = ref(cachedData);
  const invoices = ref([]);
  const loading = ref({
    subscription: false,
    invoices: false,
    limits: false,
    checkout: false,
  });
  const error = ref(null);

  const isCacheExpired = () => {
    const timestampKey = `${LOCAL_STORAGE_KEYS.RUUT_SUBSCRIPTION}:ts`;
    const timestamp = LocalStorage.get(timestampKey);

    if (!timestamp) return true;

    const isExpired = Date.now() - timestamp > CACHE_TTL;

    if (isExpired) {
      LocalStorage.remove(timestampKey);
    }

    return isExpired;
  };

  // Update subscription cache
  const updateCache = data => {
    LocalStorage.updateJsonStore(
      LOCAL_STORAGE_KEYS.RUUT_SUBSCRIPTION,
      accountId.value,
      data
    );
    LocalStorage.set(`${LOCAL_STORAGE_KEYS.RUUT_SUBSCRIPTION}:ts`, Date.now());
  };

  // Extract subscription data from account
  // Note: When DISABLE_BILLING is true, subscription_status comes from limits API
  // at the top level of the account record (not in custom_attributes)
  const extractSubscriptionFromAccount = account => {
    if (!account?.custom_attributes) return null;

    const customAttrs = account.custom_attributes;

    // When DISABLE_BILLING=true, the limits API returns subscription_status at top level
    // We prioritize this over custom_attributes.subscription_status
    const subscriptionStatus =
      account.subscription_status || customAttrs.subscription_status || 'inactive';

    // Map new format to legacy format for compatibility
    return {
      subscription: {
        status: subscriptionStatus,
        expires_at: customAttrs.subscription_ends_on,
        next_billing_date: customAttrs.subscription_ends_on,
        expiring_soon: customAttrs.subscription_expiring_soon || false,
        days_remaining_in_grace_period: customAttrs.grace_period_days || 0,
      },
      product: {
        name: account.plan_name || customAttrs.plan_name || 'Free Plan',
        payment_interval: customAttrs.payment_interval || 'monthly',
      },
      invoice: {
        total_amount: customAttrs.plan_price || '0',
        amount: customAttrs.plan_price || '0',
        currency: customAttrs.currency || 'NGN',
      },
      order: {
        quantity: customAttrs.member_count || 1,
      },
      customer: {
        email: customAttrs.customer_email || account.owner_email || '',
      },
    };
  };

  // Helper to get subscription status from reactive account or cached subscription
  const getSubscriptionStatus = () => {
    // Priority 1: Real-time status from limits API (reactive)
    // This is set when DISABLE_BILLING=true or when limits are fetched
    if (currentAccount.value?.subscription_status) {
      return currentAccount.value.subscription_status;
    }
    // Priority 2: Cached subscription data
    if (subscription.value?.subscription?.status) {
      return subscription.value.subscription.status;
    }
    return 'loading';
  };

  // Computed properties - maintain legacy interface
  const isSubscriptionActive = computed(() => {
    return getSubscriptionStatus() === 'active';
  });

  const isTrialActive = computed(() => {
    return getSubscriptionStatus() === 'trialing';
  });

  const isInGracePeriod = computed(() => {
    const status = getSubscriptionStatus();
    const expiresAt =
      currentAccount.value?.subscription_expires_at ||
      subscription.value?.subscription?.expires_at;
    if (status !== 'canceled' || !expiresAt) return false;

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const gracePeriodEnd = new Date(expiryDate);
    gracePeriodEnd.setDate(gracePeriodEnd.getDate() + 2);

    return expiryDate <= now && gracePeriodEnd >= now;
  });

  const gracePeriodDaysRemaining = computed(() => {
    if (!isInGracePeriod.value) return 0;

    const expiresAt =
      currentAccount.value?.subscription_expires_at ||
      subscription.value?.subscription?.expires_at;
    if (!expiresAt) return 0;

    const gracePeriodEnd = new Date(expiresAt);
    gracePeriodEnd.setDate(gracePeriodEnd.getDate() + 2);

    const diffTime = gracePeriodEnd - new Date();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  });

  const subscriptionExpiringSoon = computed(() => {
    return subscription.value?.subscription?.expiring_soon || false;
  });

  const subscriptionStatus = computed(() => {
    return getSubscriptionStatus();
  });

  const planName = computed(() => {
    // Priority 1: Real-time from limits API
    if (currentAccount.value?.plan_name) {
      return currentAccount.value.plan_name;
    }
    // Priority 2: Cached subscription data
    return subscription.value?.product?.name || 'Free Plan';
  });

  const planPrice = computed(() => {
    const invoice = subscription.value?.invoice;
    if (!invoice) return '0';
    return invoice.total_amount || invoice.amount || '0';
  });

  const memberCount = computed(() => {
    const order = subscription.value?.order;
    return order?.quantity || 1;
  });

  const paymentInterval = computed(() => {
    const product = subscription.value?.product;
    return product?.payment_interval || 'monthly';
  });

  const nextBillingDate = computed(() => {
    // Priority 1: Real-time from limits API
    if (currentAccount.value?.subscription_expires_at) {
      return currentAccount.value.subscription_expires_at;
    }
    // Priority 2: Cached subscription data
    return (
      subscription.value?.subscription?.expires_at ||
      subscription.value?.subscription?.next_billing_date
    );
  });

  const customerEmail = computed(() => {
    return subscription.value?.customer?.email || '';
  });

  const daysLeft = computed(() => {
    // Priority 1: Real-time from limits API
    const expiresAtStr =
      currentAccount.value?.subscription_expires_at ||
      subscription.value?.subscription?.expires_at;
    if (!expiresAtStr) return null;
    const expiresAt = new Date(expiresAtStr);
    const now = new Date();
    const diffTime = expiresAt - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  });

  // Subscription category for high-level status grouping
  const subscriptionCategory = computed(() => {
    const status = getSubscriptionStatus();
    if (['active', 'trialing'].includes(status)) return 'usable';
    if (status === 'past_due') return 'warning';
    if (status === 'canceled') return 'grace';
    if (['incomplete', 'paused'].includes(status)) return 'pending';
    if (['expired', 'revoked', 'incomplete_expired'].includes(status)) return 'blocked';
    return 'loading';
  });

  // Whether the user can use the app (not blocked)
  const canUseApp = computed(() => {
    return ['usable', 'warning', 'grace'].includes(subscriptionCategory.value);
  });

  const needsBillingPortal = computed(() => {
    const status = getSubscriptionStatus();
    return status === 'past_due' || status === 'canceled';
  });

  const needsFreshCheckout = computed(() => {
    const status = getSubscriptionStatus();
    return ['expired', 'revoked', 'incomplete_expired'].includes(status);
  });

  const needsCheckoutResume = computed(() => {
    return getSubscriptionStatus() === 'incomplete';
  });

  // Limits usage computed properties (similar to captain showBanner logic)
  const agentsUsageRatio = computed(() => {
    if (!billingLimits.value || !billingUsage.value) return 0;
    const limit = billingLimits.value.agents || 999_999;
    const used = billingUsage.value.agents || 0;
    return used / limit;
  });

  const inboxesUsageRatio = computed(() => {
    if (!billingLimits.value || !billingUsage.value) return 0;
    const limit = billingLimits.value.inboxes || 999_999;
    const used = billingUsage.value.inboxes || 0;
    return used / limit;
  });

  const domainsUsageRatio = computed(() => {
    if (!billingLimits.value || !billingUsage.value) return 0;
    const limit = billingLimits.value.domains || 999_999;
    const used = billingUsage.value.domains || 0;
    return used / limit;
  });

  // Show billing banner when any limit is at or exceeds threshold
  const showBillingBanner = computed(() => {
    if (!billingLimits.value || !billingUsage.value) return false;

    // Show banner if any resource is at 80% or higher usage (like captain)
    const AGENT_WARNING_RATIO = 0.8;
    const INBOX_WARNING_RATIO = 0.8;
    const DOMAIN_WARNING_RATIO = 1.0; // Domains are limited, warn at 100%

    return (
      agentsUsageRatio.value >= AGENT_WARNING_RATIO ||
      inboxesUsageRatio.value >= INBOX_WARNING_RATIO ||
      domainsUsageRatio.value >= DOMAIN_WARNING_RATIO
    );
  });

  // Check if a specific resource is at its limit
  const isAgentsLimitReached = computed(() => agentsUsageRatio.value >= 1.0);
  const isInboxesLimitReached = computed(() => inboxesUsageRatio.value >= 1.0);
  const isDomainsLimitReached = computed(() => domainsUsageRatio.value >= 1.0);

  // Methods
  const fetchSubscription = async (forceRefresh = false) => {
    // Skip fetch if cache is valid and not forced refresh
    if (!forceRefresh && !isCacheExpired() && subscription.value) {
      return;
    }

    loading.value.subscription = true;
    error.value = null;

    try {
      const account = getAccount(accountId.value);
      if (!account) {
        throw new Error('Account not found');
      }

      const subscriptionData = extractSubscriptionFromAccount(account);

      // Update reactive state and cache
      subscription.value = subscriptionData;
      updateCache(subscriptionData);
    } catch (err) {
      error.value = 'Failed to fetch subscription data';
    } finally {
      loading.value.subscription = false;
    }
  };

  const fetchInvoices = async () => {
    loading.value.invoices = true;
    try {
      invoices.value = [];
    } catch (err) {
    } finally {
      loading.value.invoices = false;
    }
  };

  const fetchLimits = async () => {
    // All accounts are enterprise - fetch limits for all
    // if (!isEnterprise && !isOnChatwootCloud) return;

    loading.value.limits = true;
    try {
      await store.dispatch('accounts/limits');
    } catch (err) {
      // Limits are not critical, so we don't show an alert
    } finally {
      loading.value.limits = false;
    }
  };

  const openStoredCheckoutUrl = async () => {
    loading.value.checkout = true;

    try {
      const account = getAccount(accountId.value);
      if (account?.custom_attributes?.checkout_url) {
        window.open(account.custom_attributes.checkout_url, '_blank');
      }
    } catch (err) {
      error.value = 'Failed to open checkout';
    } finally {
      loading.value.checkout = false;
    }
  };

  const cancelSubscription = async () => {
    try {
    } catch (err) {}
  };

  const refreshAllData = async (forceRefresh = false) => {
    await Promise.all([
      fetchSubscription(forceRefresh),
      fetchInvoices(),
      fetchLimits(),
    ]);
  };

  // Format currency for display
  const formatCurrency = (amount, currency = 'NGN') => {
    if (!amount) return '$0.00';

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(numAmount);
  };

  // Format date for display
  const formatDate = dateString => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Initialize data on mount - use cached data first, fetch in background
  onMounted(async () => {
    // Fetch in background if cache is expired or missing
    await refreshAllData();
  });

  return {
    // State
    subscription,
    invoices,
    loading,
    error,

    // Computed
    isSubscriptionActive,
    isTrialActive,
    isInGracePeriod,
    gracePeriodDaysRemaining,
    subscriptionExpiringSoon,
    subscriptionStatus,
    subscriptionCategory,
    canUseApp,
    needsBillingPortal,
    needsFreshCheckout,
    needsCheckoutResume,
    planName,
    planPrice,
    memberCount,
    paymentInterval,
    nextBillingDate,
    customerEmail,
    daysLeft,

    // Limits & Usage
    billingLimits,
    billingUsage,
    subscriptionInfo,
    agentsUsageRatio,
    inboxesUsageRatio,
    domainsUsageRatio,
    showBillingBanner,
    isAgentsLimitReached,
    isInboxesLimitReached,
    isDomainsLimitReached,

    // Methods
    fetchSubscription,
    fetchInvoices,
    fetchLimits,
    openStoredCheckoutUrl,
    cancelSubscription,
    refreshAllData,
    formatCurrency,
    formatDate,
  };
}
